import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  CalendarIcon, 
  TruckIcon, 
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  UsersIcon,
  BanknotesIcon,
  CogIcon,
  QrCodeIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useVendor } from '../../contexts/VendorContext';
import AdminLayout from './AdminLayout';
import AppShell from './AppShell';
import { NavKey } from '../../utils/permissions';
import { canSeeNavByPermissions, getHomePathForUser } from '../../utils/userPermissions';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { currentVendor, setCurrentVendor } = useVendor();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Clear vendor context for super_admin users
  React.useEffect(() => {
    if (user && user.role === 'super_admin' && currentVendor) {
      console.log('🔍 Clearing vendor context for super_admin user');
      setCurrentVendor(null);
      localStorage.removeItem('currentVendor');
    }
  }, [user, currentVendor, setCurrentVendor]);

  // If they open a page without permission, send them to their home
  React.useEffect(() => {
    if (!user || user.role === 'super_admin' || user.role === 'admin') {
      return;
    }
    const path = location.pathname;
    const isVendorPath = path.startsWith('/vendor');
    const checks: { key: NavKey; match: boolean }[] = [
      { key: 'dashboard', match: path === '/vendor' || path === '/' },
      { key: 'bookings', match: path.includes('/bookings') },
      { key: 'trips', match: path.includes('/trips') },
      { key: 'boarding', match: path.includes('/boarding') },
      { key: 'housing', match: path.includes('/housing') },
      { key: 'buses', match: path.includes('/buses') },
      { key: 'accommodations', match: path.includes('/accommodations') },
      { key: 'cash-closing', match: path.includes('/cash-closing') },
      { key: 'reports', match: path.includes('/reports') },
      { key: 'users', match: path.includes('/users') },
      { key: 'settings', match: path.includes('/settings') },
    ];
    const hit = checks.find((c) => c.match);
    if (hit && !canSeeNavByPermissions(user, hit.key)) {
      navigate(getHomePathForUser(user, isVendorPath || !!currentVendor), { replace: true });
    }
  }, [user, location.pathname, currentVendor, navigate]);
  
  // Render admin layout if user is system super_admin
  if (user && user.role === 'super_admin') {
    console.log('🔍 Rendering AdminLayout for super_admin user');
    return <AdminLayout>{children}</AdminLayout>;
  }

  // Determine if current user is vendor or admin
  const isVendor = currentVendor || (user && user.role !== 'super_admin');
  const baseUrl = isVendor ? '/vendor' : '';
  const role = user?.role;

  const allNavigation: { key: NavKey; name: string; href: string; icon: typeof HomeIcon; current: boolean }[] = [
    { key: 'dashboard', name: 'لوحة التحكم', href: isVendor ? '/vendor' : '/', icon: HomeIcon, current: location.pathname === (isVendor ? '/vendor' : '/') },
    { key: 'bookings', name: 'الحجوزات', href: `${baseUrl}/bookings`, icon: CalendarIcon, current: location.pathname.startsWith('/bookings') || location.pathname.startsWith('/vendor/bookings') },
    { key: 'trips', name: 'الرحلات', href: `${baseUrl}/trips`, icon: TruckIcon, current: location.pathname.startsWith('/trips') || location.pathname.startsWith('/vendor/trips') },
    { key: 'buses', name: 'الباصات', href: `${baseUrl}/buses`, icon: BuildingOfficeIcon, current: location.pathname.startsWith('/buses') || location.pathname.startsWith('/vendor/buses') },
    { key: 'accommodations', name: 'السكن', href: `${baseUrl}/accommodations`, icon: BuildingStorefrontIcon, current: location.pathname.startsWith('/accommodations') || location.pathname.startsWith('/vendor/accommodations') },
    { key: 'boarding', name: 'بوابة الصعود', href: `${baseUrl}/boarding`, icon: QrCodeIcon, current: location.pathname.startsWith('/boarding') || location.pathname.startsWith('/vendor/boarding') },
    { key: 'housing', name: 'التسكين', href: `${baseUrl}/housing`, icon: BuildingOffice2Icon, current: location.pathname.startsWith('/housing') || location.pathname.startsWith('/vendor/housing') },
    { key: 'cash-closing', name: 'إغلاق الكاشير', href: `${baseUrl}/cash-closing`, icon: BanknotesIcon, current: location.pathname.startsWith('/cash-closing') || location.pathname.startsWith('/vendor/cash-closing') },
    { key: 'reports', name: 'التقارير', href: `${baseUrl}/reports`, icon: ChartBarIcon, current: location.pathname.startsWith('/reports') || location.pathname.startsWith('/vendor/reports') },
    { key: 'users', name: 'المستخدمين', href: `${baseUrl}/users`, icon: UsersIcon, current: location.pathname.startsWith('/users') || location.pathname.startsWith('/vendor/users') },
    { key: 'settings', name: 'الإعدادات', href: `${baseUrl}/settings`, icon: CogIcon, current: location.pathname.startsWith('/settings') || location.pathname.startsWith('/vendor/settings') },
  ];

  const navigation = allNavigation.filter((item) => canSeeNavByPermissions(user, item.key));

  const handleLogout = () => {
    logout();
    navigate(currentVendor || role !== 'super_admin' ? '/vendor-login' : '/login');
  };

  return (
    <AppShell
      brandTitle={currentVendor?.companyName || currentVendor?.vendorName || 'مكتب الحرمين'}
      brandSubtitle="نظام إدارة الحج والعمرة"
      brandIcon={BuildingOffice2Icon}
      navigation={navigation}
      userName={user?.fullName}
      userSubtitle={user?.branch?.name}
      userCaption={currentVendor?.vendorName || user?.branch?.name}
      onLogout={handleLogout}
    >
      {children}
    </AppShell>
  );
};

export default Layout; 